import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box } from '@mui/system';
import { getWritingPlan } from 'src/configuration/writing-plans';
import { getBlockType } from 'src/configuration/blocks';
import { parseMeta } from 'src/utils/assignmentMetrics';
import * as AssignmentsAPI from '../../api/Assignments';
import HandoutContainer from './handoutStyles';

interface HandoutBlock {
  typeId: string;
  label: string;
  placeholder: string;
}

/**
 * Single-page printable handout for an assignment. Teacher opens this in a
 * new tab from the assignment-review page, then prints via the browser's
 * native print dialog (Cmd+P → Save as PDF). No PDF library — the print
 * stylesheet does the work.
 *
 * Layout (top to bottom):
 *   - Name / Date line
 *   - Assignment title
 *   - Prompt (instructions)
 *   - Per-block writing boxes with placeholder/sentence-starter in light gray
 *   - Word-support pill rows: Heart, Important, Signal (whichever are enabled)
 */
export default function AssignmentHandout() {
  const { id: assignmentId } = useParams();
  const [assignment, setAssignment] = useState<any>();
  const [isLoading, setIsLoading] = useState(true);
  // Sentence starters are pedagogically helpful for kids who freeze on a
  // blank page — but some teachers want a totally clean handout. Default on,
  // one-click to flip.
  const [showStarters, setShowStarters] = useState(true);

  useEffect(() => {
    if (!assignmentId) return;
    AssignmentsAPI.getById(assignmentId)
      .then((response) => {
        if (response.status === 200) setAssignment(response.data.data);
      })
      .finally(() => setIsLoading(false));
  }, [assignmentId]);

  const meta = useMemo(
    () => parseMeta(assignment?.['base-document']?.meta),
    [assignment],
  );

  // Resolve the list of blocks to render on the handout. We pull from the
  // base document's actual page content when present (that's where per-block
  // placeholder/sentence-starter text lives), and fall back to the writing
  // plan's blockTypeIds + the block config's generic placeholder when not.
  const handoutBlocks: HandoutBlock[] = useMemo(() => {
    if (!assignment?.['base-document']) return [];

    const pages = assignment['base-document'].version?.content?.pages
      || assignment['base-document'].version?.pages
      || [];

    const firstPageBlocks = pages[0]?.grid?.blocks || [];

    // Prefer the actual blocks from the base document — they carry the
    // teacher's customized placeholder/sentence-starter text.
    if (firstPageBlocks.length) {
      return firstPageBlocks
        .filter((b: any) => {
          // Skip image-only blocks on a paper handout (no room to "draw" a
          // photo). Title is text; we keep it.
          const t = b.typeId;
          return t !== 'Image' && t !== 'ImageCaption';
        })
        .map((b: any) => {
          const type = getBlockType(b);
          return {
            typeId: b.typeId,
            label: type?.label || b.typeId,
            placeholder: b.properties?.placeholder || type?.configuration?.placeholder?.toString() || '',
          };
        });
    }

    // Fallback: writing-plan template structure.
    if (meta.writingPlan) {
      const plan = getWritingPlan(meta.writingPlan);
      if (plan) {
        return plan.blockTypeIds
          .filter((id) => id !== 'Image' && id !== 'ImageCaption')
          .map((typeId) => {
            const type = getBlockType(typeId);
            return {
              typeId,
              label: type?.label || String(typeId),
              placeholder: type?.configuration?.placeholder?.toString() || '',
            };
          });
      }
    }

    return [];
  }, [assignment, meta.writingPlan]);

  const signalWords: string[] = useMemo(() => {
    if (!meta.writingPlan || !meta.hasSignalWords) return [];
    const plan = getWritingPlan(meta.writingPlan);
    return plan?.signalWords || [];
  }, [meta.writingPlan, meta.hasSignalWords]);

  const importantWords: string[] = meta.hasImportantWords
    ? (meta.importantWords || [])
    : [];
  const heartWords: string[] = meta.hasHeartWords
    ? (meta.heartWords || [])
    : [];

  if (isLoading) {
    return (
      <HandoutContainer>
        <Box className="handout-loading">Loading…</Box>
      </HandoutContainer>
    );
  }
  if (!assignment) {
    return (
      <HandoutContainer>
        <Box className="handout-loading">Couldn&apos;t find that assignment.</Box>
      </HandoutContainer>
    );
  }

  return (
    <HandoutContainer>
      <Box className="handout-page">
        <Box className="handout-controls no-print">
          <Box className="starter-toggle">
            <div className="starter-toggle-copy">
              <strong>
                {showStarters
                  ? 'Sentence starters are on.'
                  : 'Sentence starters are off.'}
              </strong>
              <span>
                {showStarters
                  ? ' Light-gray prompts give students a nudge when they freeze on a blank page.'
                  : ' Clean blank boxes — students start from scratch.'}
              </span>
            </div>
            <button
              type="button"
              className="starter-toggle-btn"
              onClick={() => setShowStarters((v) => !v)}
            >
              {showStarters ? 'Hide starters' : 'Show starters'}
            </button>
          </Box>
          <button
            type="button"
            className="print-button"
            onClick={() => window.print()}
          >
            🖨 Print / Save as PDF
          </button>
        </Box>

        <Box className="name-row">
          <span className="name-line">
            <span className="name-label">Name</span>
            <span className="name-blank" />
          </span>
          <span className="name-line">
            <span className="name-label">Date</span>
            <span className="name-blank short" />
          </span>
        </Box>

        <h1 className="handout-title">{assignment.name}</h1>

        {assignment.instructions && (
          <Box className="prompt-box">
            <div className="prompt-label">Your assignment:</div>
            <div className="prompt-text">{assignment.instructions}</div>
          </Box>
        )}

        <Box className="blocks">
          {handoutBlocks.map((b, i) => (
            <Box key={`${b.typeId}-${i}`} className={`block-area block-${b.typeId.toLowerCase()}`}>
              <div className="block-label">{b.label}</div>
              <div className="block-box">
                {showStarters && b.placeholder && (
                  <span className="block-placeholder">{b.placeholder}</span>
                )}
              </div>
            </Box>
          ))}
        </Box>

        {(heartWords.length > 0 || importantWords.length > 0 || signalWords.length > 0) && (
          <Box className="word-supports">
            {heartWords.length > 0 && (
              <Box className="word-row heart">
                <div className="word-label">
                  <span aria-hidden="true">♥</span> Heart Words
                </div>
                <div className="word-pills">
                  {heartWords.map((w) => (
                    <span key={w} className="word-pill">{w}</span>
                  ))}
                </div>
              </Box>
            )}
            {importantWords.length > 0 && (
              <Box className="word-row important">
                <div className="word-label">
                  <span aria-hidden="true">★</span> Important Words
                </div>
                <div className="word-pills">
                  {importantWords.map((w) => (
                    <span key={w} className="word-pill">{w}</span>
                  ))}
                </div>
              </Box>
            )}
            {signalWords.length > 0 && (
              <Box className="word-row signal">
                <div className="word-label">
                  <span aria-hidden="true">»</span> Signal Words
                </div>
                <div className="word-pills">
                  {signalWords.map((w) => (
                    <span key={w} className="word-pill">{w}</span>
                  ))}
                </div>
              </Box>
            )}
          </Box>
        )}

        <Box className="handout-footer">
          <span>Pressto · joinpressto.com</span>
        </Box>
      </Box>
    </HandoutContainer>
  );
}
